import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { firstStepIndex, getTour, pathMatchesRoute, readCurrentDealRoom, resolveStoredTour, shouldAdvanceWaitStep, shouldSkipForOrigin, tourSessionAt } from '@/tutorials/tours';
import { markLessonComplete } from '@/tutorials/progress';
import { readActiveTour, writeActiveTour } from '@/tutorials/tourSession';

const ProductTourContext = createContext(null);

function navigateForStep(navigate, route) {
  if (!route) return;
  if (pathMatchesRoute(window.location.pathname, route)) return;
  const target = Array.isArray(route) ? route[0] : route;
  if (target === '/transaction') {
    if (!window.location.pathname.startsWith('/transaction/')) navigate('/dashboard');
    return;
  }
  navigate(target);
}

export function ProductTourProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(() => (
    resolveStoredTour(readActiveTour(), typeof window !== 'undefined' ? window.location.pathname : '')
  ));
  const waitAdvanceKey = useRef('');

  const persist = useCallback((next) => {
    setActive(next);
    writeActiveTour(next);
  }, []);

  const startTour = useCallback((tourId) => {
    const tour = getTour(tourId);
    if (!tour) return;
    waitAdvanceKey.current = '';
    const dealRoom = window.location.pathname.startsWith('/transaction/')
      ? readCurrentDealRoom()
      : '';
    const originPath = window.location.pathname;
    const step = firstStepIndex(tour, originPath, dealRoom);
    persist(tourSessionAt(tour.id, step, {
      originStep: step,
      originPath,
      originDealRoom: dealRoom,
    }));
    navigateForStep(navigate, tour.steps[step]?.route);
  }, [navigate, persist]);

  const stopTour = useCallback(() => {
    waitAdvanceKey.current = '';
    persist(null);
  }, [persist]);

  const goToStep = useCallback((index) => {
    setActive((prev) => {
      if (!prev) return prev;
      const tour = getTour(prev.id);
      if (!tour) return null;
      if (index >= tour.steps.length) {
        markLessonComplete(prev.id);
        writeActiveTour(null);
        return null;
      }
      const originFloor = Number.isInteger(prev.originStep) ? prev.originStep : 0;
      const safe = Math.max(originFloor, index);
      const next = tourSessionAt(prev.id, safe, prev);
      writeActiveTour(next);
      navigateForStep(navigate, tour.steps[safe]?.route);
      return next;
    });
  }, [navigate]);

  const nextStep = useCallback(() => {
    setActive((prev) => {
      if (!prev) return prev;
      const tour = getTour(prev.id);
      if (!tour) return null;
      let index = prev.step + 1;
      while (index < tour.steps.length && shouldSkipForOrigin(tour.steps[index], prev, window.location.pathname)) {
        index += 1;
      }
      if (index >= tour.steps.length) {
        markLessonComplete(prev.id);
        writeActiveTour(null);
        return null;
      }
      const next = tourSessionAt(prev.id, index, prev);
      writeActiveTour(next);
      navigateForStep(navigate, tour.steps[index]?.route);
      return next;
    });
  }, [navigate]);

  const prevStep = useCallback(() => {
    setActive((prev) => {
      if (!prev) return prev;
      const tour = getTour(prev.id);
      if (!tour) return null;
      const originFloor = Number.isInteger(prev.originStep) ? prev.originStep : 0;
      let index = prev.step - 1;
      while (index >= originFloor && shouldSkipForOrigin(tour.steps[index], prev, window.location.pathname)) {
        index -= 1;
      }
      if (index < originFloor) return prev;
      const next = tourSessionAt(prev.id, index, prev);
      writeActiveTour(next);
      navigateForStep(navigate, tour.steps[index]?.route);
      return next;
    });
  }, [navigate]);

  useEffect(() => {
    if (!active) {
      waitAdvanceKey.current = '';
      return;
    }
    const tour = getTour(active.id);
    const step = tour?.steps[active.step];
    if (!shouldAdvanceWaitStep(step, location.pathname)) return;
    const key = `${active.id}:${active.step}`;
    if (waitAdvanceKey.current === key) return;
    waitAdvanceKey.current = key;
    nextStep();
  }, [active, location.pathname, nextStep]);

  const value = useMemo(() => {
    const tour = active ? getTour(active.id) : null;
    const step = tour ? tour.steps[active.step] : null;
    return {
      active,
      tour,
      step,
      stepIndex: active?.step ?? 0,
      startTour,
      stopTour,
      nextStep,
      prevStep,
      goToStep,
    };
  }, [active, startTour, stopTour, nextStep, prevStep, goToStep]);

  return (
    <ProductTourContext.Provider value={value}>
      {children}
    </ProductTourContext.Provider>
  );
}

export function useProductTour() {
  return useContext(ProductTourContext);
}
